import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PaymentProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Payment } from "assets/SideBarIcons/money-transfer.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import {
  GetPaymentByIdResponse,
  GetPaymentInvoiceResponse,
  GetPaymentsCreditResponse,
} from "subModule/src/services/payment/interface";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import BasicDatePicker from "common/Pickers/Pickers";
import { useGetClientsDropdownService } from "subModule/src/services/client/useClient";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { PaymentInvoicesTable } from "components/Payment/PaymentInvoicesTable";
import StyledInput from "common/SearchField/Search";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import { useGetPaymentModesDropdownService } from "subModule/src/services/payment/usePayments";
import { useGetBankAccountsDropdownService } from "subModule/src/services/bankaccount/useBankAccount";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData {
  [key: string]: any;
}
export default function PaymentModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "customers",
    moduleLabelKey: "payments",
    moduleHref: "/payments",
    entityFallbackLabelKey: "referenceNo",
    isEdit: !!params?.id,
    action: params?.id ? "edit" : undefined,
  });
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  /** When payment-mode filtered accounts are empty, reload without paymentMode. */
  const [useAllAccountsFallback, setUseAllAccountsFallback] = useState(false);
  const useAllAccountsFallbackRef = useRef(false);
  const pendingAccountsFallbackRefetch = useRef(false);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      adjustmentType: { value: 591, label: t("FIFO") },
      invoices: [],
    },
  });
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const [credit, setCredit] = useState<number>(0);
  const [invoicesData, setInvoicesData] = useState<any>({});
  const [searchInvoice, setSearchInvoice] = useState("");
  const [searchTermInvoice, setSearchTermInvoice] = useState<any>(null);
  const [resetInvoices, setResetInvoices] = useState<boolean>(false);
  const [loadingInvoices, setLoadingInvoices] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(5);
  const [invoicePageUpdate, setInvoicePageUpdate] = useState<boolean>(false);
  const { data: clientsData, refetch: clientsRefetch } =
    useGetClientsDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      type: [160, 170, 180],
      search: dropdownSearchTerm,
    });
  const { data: paymentModesData, refetch: paymentModesRefetch } =
    useGetPaymentModesDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: stationsData, refetch: stationsRefetch } =
    useGetStationDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      name: dropdownSearchTerm,
    });
  const selectedStationId = watch("stationId")?.value;
  const selectedPaymentModeId = watch("paymentMode")?.value;
  // const canLoadAccounts = Boolean(selectedStationId && selectedPaymentModeId);
  const canLoadAccounts = Boolean(selectedPaymentModeId);
  const { data: bankAccountsData, refetch: bankAccountsRefetch } =
    useGetBankAccountsDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      search: dropdownSearchTerm,
      stationId: selectedStationId,
      paymentMode: useAllAccountsFallback ? undefined : selectedPaymentModeId,
      active: true,
    });
  const clients = (clientsData?.pages[0]?.data?.content || []).map(
    (item?: { id?: number; name?: string; accountId?: number | null }) => ({
      value: item?.id,
      label: item?.name,
      accountId: item?.accountId,
    }),
  );
  const paymentModes = (paymentModesData?.pages[0]?.data?.content || []).map(
    (item: any) => ({ value: item.id, label: item.name }),
  );
  const stations = (stationsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; name: string }) => ({
      value: item.id,
      label: item.name,
    }),
  );
  const bankAccounts = (bankAccountsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; accountTitle: string }) => ({
      value: item.id,
      label: item.accountTitle,
    }),
  );
  const adjustmentTypeOptions = [
    { value: 591, label: t("FIFO") },
    { value: 592, label: t("MANUAL") },
  ];
  const invoicesColumns = [
    { id: "invoicesId", label: "", numeric: false, disablePadding: false },
    { id: "invoiceNo", label: t("invoiceNo"), numeric: false },
    { id: "balanceAmount", label: t("balanceAmount"), numeric: false },
    { id: "totalAmount", label: t("totalAmount"), numeric: false },
  ];
  useEffect(() => {
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    const getData = setTimeout(async () => {
      if (searchTermInvoice !== null && searching) {
        fetchInvoices(watch("clientId").value, 1, pageSize, searchTermInvoice);
      }
    }, 300);
    return () => clearTimeout(getData);
  }, [searchTermInvoice, searching]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  useEffect(() => {
    useAllAccountsFallbackRef.current = useAllAccountsFallback;
  }, [useAllAccountsFallback]);
  useEffect(() => {
    setUseAllAccountsFallback(false);
    useAllAccountsFallbackRef.current = false;
  }, [selectedPaymentModeId]);
  useEffect(() => {
    if (
      !useAllAccountsFallback ||
      !pendingAccountsFallbackRefetch.current ||
      !canLoadAccounts
    )
      return;
    pendingAccountsFallbackRefetch.current = false;
    setSearchLoading("accounts");
    bankAccountsRefetch().finally(() => setSearchLoading(null));
  }, [useAllAccountsFallback, canLoadAccounts, bankAccountsRefetch]);
  const getAccountsContent = (res: any) =>
    res?.data?.pages?.[0]?.data?.content ||
    res?.pages?.[0]?.data?.content ||
    [];
  const maybeFallbackToAllAccounts = (res: any, action: string) => {
    if (
      action === "loadNext" ||
      useAllAccountsFallbackRef.current ||
      !selectedPaymentModeId
    )
      return false;
    const content = getAccountsContent(res);
    if (content.length > 0) return false;
    pendingAccountsFallbackRefetch.current = true;
    setUseAllAccountsFallback(true);
    return true;
  };
  const getByID = () => {
    PaymentProfileService.getPaymentByID(+(params as any)?.id)
      .then((res: GetPaymentByIdResponse) => {
        if (res.success) {
          setTimeout(() => fetchCredit(res?.data?.clientId || 0), 700);
          if (res?.data?.adjustmentType === 592)
            fetchInvoices(res?.data?.clientId || 0, 1, 5);
          reset({
            ...(res?.data || {}),
            clientId: {
              value: res.data?.clientId,
              label: res.data?.clientName,
            },
            stationId: res.data?.stationId
              ? {
                  value: res.data?.stationId,
                  label: (res.data as any)?.stationName || res.data?.stationId,
                }
              : null,
            paymentMode: {
              value: res.data?.paymentMode,
              label: res?.data?.paymentModeTitle,
            },
            bankAccountId: res.data?.bankAccountId
              ? {
                  value: res.data?.bankAccountId,
                  label:
                    res.data?.bankAccountTitle ||
                    res.data?.accountTitle ||
                    res.data?.bankAccountId,
                }
              : null,
            adjustmentType: {
              value: res.data?.adjustmentType,
              label:
                adjustmentTypeOptions?.find(
                  (option: any) => option.value === res?.data?.adjustmentType,
                )?.label || res.data?.adjustmentType,
            },
          });
          captureLabel(
            res?.data?.paymentNo ||
              res?.data?.referenceNo ||
              res?.data?.clientName,
          );
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const fetchCredit = (clientId: number) => {
    PaymentProfileService.getPaymentsCreditSales({
      clientId: clientId,
      accountId: watch("accountId") || null,
    })
      .then((response: GetPaymentsCreditResponse) => {
        if (response.success) {
          setCredit(response?.data || 0);
        } else {
          setLoading(false);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const fetchInvoices = (
    clientId: number,
    page: number,
    size: number,
    invoiceNo?: any,
  ) => {
    PaymentProfileService.getPaymentsInvoice({
      page: page,
      size: size,
      clientId: clientId,
      accountId: watch("accountId") || null,
      invoiceNo: invoiceNo,
    })
      .then((response: GetPaymentInvoiceResponse) => {
        if (response.success) {
          setInvoicesData(response?.data || {});
        } else {
          setLoading(false);
          enqueueSnackbar(<AlertMessages text={response.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
        enqueueSnackbar(
          <AlertMessages
            text={err?.data?.message || err || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => {
        setLoadingInvoices(false);
        setSearching(false);
        setInvoicePageUpdate(false);
      });
  };
  const clearSearchInvoice = () => {
    setSearching(true);
    setSearchInvoice("");
    setSearchTermInvoice("");
    if (searchTermInvoice) {
      handleSearch("");
    }
  };
  const handleSearch = (search: string) => {
    setSearching(true);
    setSearchTermInvoice(search);
  };
  const handleSearchClick = () => {
    const trimmedSearch = searchInvoice.trim();
    if (trimmedSearch !== "") {
      setSearching(true);
      handleSearch(trimmedSearch);
    }
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInvoice(event.target.value.trim());
    if (!event.target.value.trim()) handleSearch("");
  };
  const handleChangePage = (page: number) => {
    setInvoicePageUpdate(true);
    setPageSize(pageSize);
    fetchInvoices(
      watch("clientId").value,
      page + 1,
      pageSize,
      searchTermInvoice,
    );
  };
  const handleChangeRows = (size: number) => {
    setInvoicePageUpdate(true);
    setPageSize(size);
    fetchInvoices(watch("clientId").value, 1, size, searchTermInvoice);
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    Object.keys(data).forEach((key) => {
      if (/^invoices\d+$/.test(key)) {
        delete data[key];
      }
    });
    data.invoices = (data.invoices || []).map((i: any) => i.id);
    if (params?.id) {
      PaymentProfileService.updatePayment(+params?.id, {
        ...data,
        clientId: data.clientId.value,
        paymentMode: data?.paymentMode?.value || null,
        adjustmentType: data.adjustmentType.value,
        bankAccountId: data?.bankAccountId?.value || null,
      })
        .then((response: any) => {
          if (response.success) {
            captureLabel(
              data?.referenceNo ||
                data?.clientId?.label ||
                response?.data?.paymentNo,
            );
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("Payment") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
          setIsBtnEnabled(true);
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      PaymentProfileService.createPayment({
        ...data,
        clientId: data.clientId.value,
        paymentMode: data?.paymentMode?.value || null,
        adjustmentType: data.adjustmentType.value,
        stationId: data.stationId.value,
        bankAccountId: data?.bankAccountId?.value || null,
      })
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("Payment") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext" | "open",
    search?: string,
  ) => {
    if (action === "open") {
      setDropdownPage(1);
      setDropdownSearchTerm("");
      setSearchLoading(typeName);
    } else if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      () => {
        if (typeName === "accounts") {
          if (!canLoadAccounts) {
            setSearchLoading(null);
            return;
          }
          bankAccountsRefetch()
            .then((res) => {
              if (maybeFallbackToAllAccounts(res, action)) return;
              setSearchLoading(null);
            })
            .catch(() => setSearchLoading(null));
        } else if (typeName === "customers")
          clientsRefetch().finally(() => setSearchLoading(null));
        else if (typeName === "paymentModes")
          paymentModesRefetch().finally(() => setSearchLoading(null));
        else if (typeName === "stations")
          stationsRefetch().finally(() => setSearchLoading(null));
      },
      action === "open" ? 0 : 300,
    );
  };
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("editPayment") : t("addPayment")}
        icon={<Payment height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_PAYMENT_CREATE")}
      />
      {loading ? (
        <Loader />
      ) : (
        <SimpleModal
          visable={true}
          title={params?.id ? "updatePayment" : "addPayment"}
          isButtonVisable={true}
          isVisableBtn={IsBtnEnabled}
          btnclosetext="Back"
          buttonText={params?.id ? "update" : "submit"}
          attBtnNotshow={false}
          notVisableBackbtn={true}
          showPortList={handleSubmit(onSubmit)}
          formSubmit={handleSubmit(onSubmit)}
          content={
            <Grid
              container
              component="form"
              id="form"
              spacing={3}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Grid item xs={12} md={3}>
                <Controller
                  name="clientId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("Client") + " *"}
                      defaultPlaceholder="phSelectCustomer"
                      {...field}
                      options={clients}
                      isSearchable
                      onMenuOpen={() =>
                        handleDropdownActions("customers", "open")
                      }
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setValue(
                          "accountId",
                          selectedOption?.accountId || null,
                        );
                        setResetInvoices(true);
                        setTimeout(
                          () => fetchCredit(selectedOption.value),
                          1000,
                        );
                        if (watch("adjustmentType").value === 592) {
                          setLoadingInvoices(true);
                          fetchInvoices(selectedOption.value, 1, pageSize);
                        }
                      }}
                      error={errors.clientId?.message as string}
                      isLoading={searchLoading === "customers"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "customers",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || dropdownPage > 1)
                            handleDropdownActions("customers", "searchIt", "");
                          else setDropdownSearchTerm("");
                          setDropdownPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(clientsData?.pages[0]?.data as any).last)
                          handleDropdownActions("customers", "loadNext");
                      }}
                      page={dropdownPage}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="stationId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("station") + " *"}
                      defaultPlaceholder="phSelectStation"
                      {...field}
                      options={stations}
                      isClearable
                      isSearchable
                      onMenuOpen={() =>
                        handleDropdownActions("stations", "open")
                      }
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setValue("bankAccountId", null);
                      }}
                      error={errors.stationId?.message as string}
                      isLoading={searchLoading === "stations"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions("stations", "searchIt", search);
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (dropdownSearchTerm || dropdownPage > 1)
                            handleDropdownActions("stations", "searchIt", "");
                          else setDropdownSearchTerm("");
                          setDropdownPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(stationsData?.pages[0]?.data as any).last)
                          handleDropdownActions("stations", "loadNext");
                      }}
                      page={dropdownPage}
                    />
                  )}
                />
              </Grid>
              {watch("useCredit") ? null : (
                <>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="paymentMode"
                      control={control}
                      defaultValue={null}
                      rules={{ required: t("This field is required.") }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("PaymentMode") + " *"}
                          defaultPlaceholder="phSelectPaymentMode"
                          {...field}
                          options={paymentModes}
                          isSearchable
                          onMenuOpen={() =>
                            handleDropdownActions("paymentModes", "open")
                          }
                          onChange={(selectedOption: any) => {
                            field.onChange(selectedOption);
                            setValue("bankAccountId", null);
                            setUseAllAccountsFallback(false);
                            useAllAccountsFallbackRef.current = false;
                          }}
                          error={errors.paymentMode?.message as string}
                          isLoading={searchLoading === "paymentModes"}
                          onInputChange={(
                            search: string,
                            { action }: { action: string },
                          ) => {
                            if (
                              action === "input-change" ||
                              (action !== "set-value" &&
                                action !== "menu-close" &&
                                action !== "input-blur")
                            )
                              handleDropdownActions(
                                "paymentModes",
                                "searchIt",
                                search,
                              );
                            else if (
                              action === "input-blur" ||
                              action === "menu-close"
                            ) {
                              if (dropdownSearchTerm || dropdownPage > 1)
                                handleDropdownActions(
                                  "paymentModes",
                                  "searchIt",
                                  "",
                                );
                              else setDropdownSearchTerm("");
                              setDropdownPage(1);
                            }
                          }}
                          onMenuScrollToBottom={() => {
                            if (!(paymentModesData?.pages[0]?.data as any).last)
                              handleDropdownActions("paymentModes", "loadNext");
                          }}
                          page={dropdownPage}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="bankAccountId"
                      control={control}
                      defaultValue={null}
                      rules={{ required: t("This field is required.") }}
                      render={({ field }) => (
                        <Box>
                          <DynamicSelect
                            label={t("ACCOUNT") + " *"}
                            defaultPlaceholder="phSelectAccount"
                            {...field}
                            options={bankAccounts}
                            isClearable
                            isSearchable
                            isDisabled={!canLoadAccounts}
                            onMenuOpen={() => {
                              if (canLoadAccounts)
                                handleDropdownActions("accounts", "open");
                            }}
                            onChange={(selectedOption: any) => {
                              field.onChange(selectedOption);
                            }}
                            error={
                              canLoadAccounts
                                ? (errors.bankAccountId?.message as string)
                                : undefined
                            }
                            isLoading={searchLoading === "accounts"}
                            onInputChange={(
                              search: string,
                              { action }: { action: string },
                            ) => {
                              if (!canLoadAccounts) return;
                              if (
                                action === "input-change" ||
                                (action !== "set-value" &&
                                  action !== "menu-close" &&
                                  action !== "input-blur")
                              )
                                handleDropdownActions(
                                  "accounts",
                                  "searchIt",
                                  search,
                                );
                              else if (
                                action === "input-blur" ||
                                action === "menu-close"
                              ) {
                                if (dropdownSearchTerm || dropdownPage > 1)
                                  handleDropdownActions(
                                    "accounts",
                                    "searchIt",
                                    "",
                                  );
                                else setDropdownSearchTerm("");
                                setDropdownPage(1);
                              }
                            }}
                            onMenuScrollToBottom={() => {
                              if (
                                canLoadAccounts &&
                                !(bankAccountsData?.pages[0]?.data as any).last
                              )
                                handleDropdownActions("accounts", "loadNext");
                            }}
                            page={dropdownPage}
                          />
                          {!canLoadAccounts ? (
                            <Typography
                              className="font-Saira italic font-[500] text-[11px] mt-1 text-start"
                              sx={{ color: "text.secondary" }}
                            >
                              {t("selectPaymentModeToEnableAccount")}
                            </Typography>
                          ) : null}
                        </Box>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      type="number"
                      {...register("amount", {
                        required: t("This field is required."),
                        validate: (value) =>
                          value > 0 || t("amountGreaterThanZero"),
                      })}
                      label={t("amount") + " *"}
                      placeholder={t("phPaymentAmount")}
                      title={t("amount")}
                      fullWidth
                      error={!!errors.amount}
                      helperText={errors.amount?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("referenceNo")}
                  label={t("referenceNo")}
                  placeholder={t("phPaymentReferenceNo")}
                  title={t("referenceNo")}
                  fullWidth
                  error={!!errors.referenceNo}
                  helperText={errors.referenceNo?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="adjustmentType"
                  control={control}
                  defaultValue={null}
                  rules={{
                    required: t("This field is required."),
                    validate: (value) => {
                      if (
                        value?.value === 592 &&
                        watch("invoices")?.length === 0
                      ) {
                        return t("atLeastOneInvoice");
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("adjustmentType")}
                      {...field}
                      options={adjustmentTypeOptions}
                      isSearchable
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setResetInvoices(true);
                        if (watch("clientId") && selectedOption.value === 592) {
                          setLoadingInvoices(true);
                          fetchInvoices(watch("clientId").value, 1, pageSize);
                        }
                      }}
                      error={errors.adjustmentType?.message as string}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="paymentDate"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? t("Invalid Date") : true;
                    },
                  }}
                  render={({ field }) => (
                    <BasicDatePicker
                      type="datePicker"
                      {...field}
                      label={t("paymentDate") + " *"}
                      fullWidth
                      onChange={(value: any) =>
                        field.onChange(
                          new Date(value)
                            .toLocaleDateString("en-CA", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })
                            ?.split("/")
                            .reverse()
                            .join("-"),
                        )
                      }
                      required={false}
                      error={errors.paymentDate?.message as string}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="recipientDate"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? t("Invalid Date") : true;
                    },
                  }}
                  render={({ field }) => (
                    <BasicDatePicker
                      type="datePicker"
                      {...field}
                      label={t("recipentDate") + " *"}
                      fullWidth
                      onChange={(value: any) =>
                        field.onChange(
                          new Date(value)
                            .toLocaleDateString("en-CA", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })
                            ?.split("/")
                            .reverse()
                            .join("-"),
                        )
                      }
                      required={false}
                      error={errors.recipientDate?.message as string}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("remarks")}
                  label={t("remarks")}
                  placeholder={t("phPaymentRemarks")}
                  title={t("remarks")}
                  fullWidth
                  error={!!errors.remarks}
                  helperText={errors.remarks?.message as string}
                  InputLabelProps={{ shrink: true }}
                  multiline
                  rows={1}
                  sx={{
                    "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                      width: "100%",
                      paddingRight: "0px",
                      paddingBottom: 0,
                    },
                    "& .MuiInputBase-inputMultiline": {
                      resize: "vertical",
                      minHeight: "29px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="useCredit"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <Box sx={{ "& label": { width: "fit-content" } }}>
                      <CommonCheckbox
                        {...field}
                        label={t("useCredit")}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          field.onChange(event.target.checked);
                          setValue("paymentMode", null);
                          setValue("amount", 0);
                          setValue("bankAccountId", null);
                        }}
                        checked={field.value}
                        color={themeColors?.primary}
                      />
                    </Box>
                  )}
                />
                {watch("clientId") ? (
                  <Typography className="font-Saira text-start font-[600] text-[17px] leading-[22px] mt-[-10px]">
                    {t("creditAmount")} = {credit || 0}
                  </Typography>
                ) : null}
              </Grid>
              {watch("clientId") && watch("adjustmentType").value === 592 ? (
                <Grid
                  container
                  spacing={2}
                  className="mt-[5px] ps-[25px] items-center relative"
                >
                  {loadingInvoices ? (
                    <CircularProgress
                      className="block m-auto"
                      sx={{ color: themeColors?.primary, width: "100%" }}
                      size={24}
                    />
                  ) : (
                    <>
                      {searching || invoicePageUpdate ? <Loader /> : null}
                      <Grid item xs={12} md={7} lg={5}>
                        <StyledInput
                          fullWidth
                          placeholder={t("phInvoiceNo")}
                          handleSearchClick={handleSearchClick}
                          handleChange={handleInputChange}
                          value={searchInvoice}
                          clearSearch={clearSearchInvoice}
                        />
                      </Grid>
                      {watch(`invoices`)?.length > 0 ? (
                        <Grid item xs={12} md={5} lg={7}>
                          <Typography
                            className="font-semibold font-Saira text-[14px] flex gap-1 justify-end"
                            sx={{ color: themeColors?.primary }}
                          >
                            <span className="font-medium">
                              {t("total") + " " + t("dueAmount")}
                            </span>
                            <span>:</span>
                            <span className="font-bold">
                              {new Intl.NumberFormat("en", {
                                notation: "standard",
                                maximumFractionDigits: 2,
                              }).format(
                                (watch("invoices") || []).reduce(
                                  (sum: number, inv: any) =>
                                    sum + (inv.balanceAmount || 0),
                                  0,
                                ),
                              )}
                            </span>
                          </Typography>
                        </Grid>
                      ) : null}
                      <Grid item xs={12}>
                        <PaymentInvoicesTable
                          rows={invoicesData?.content || []}
                          columns={invoicesColumns}
                          handleChangePage={handleChangePage}
                          handleChangeRows={handleChangeRows}
                          tableData={invoicesData || {}}
                          form={{
                            register,
                            control,
                            handleSubmit,
                            formState: { errors, dirtyFields, isDirty },
                            reset,
                            watch,
                            setValue,
                          }}
                          {...{ resetInvoices, pageSize }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              ) : null}
            </Grid>
          }
        />
      )}
    </>
  );
}
